<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import {
  RouterLinkBack,
  VAvatar,
  VBadge,
  VButtonInvisible,
  VPopUpModal,
  VMailTo,
  VSection,
  VSkeletonBar,
} from '@/modules/shared/components'
import { useModal } from '@/modules/shared/composables/use-modal'
import { Status } from '@/modules/screening/types'
import ScreeningCheckWrapper from '@/modules/screening/component/CheckWrapper.vue'
import { useWorkspaceStore } from '@/modules/workspace/stores/workspace-store'
import {
  useInvestingInvestorStore,
  Investor,
  accreditatedStatusInfo,
  amlStatusInfo,
  profileStatusInfo,
} from '../stores/investor-store'
import { rails_url } from '@/modules/shared/utils/rails'

const skeleton = ref(true)

const route = useRoute()

const cid = `individual:${route.params.individual_id}`

const workspaceStore = useWorkspaceStore()

const authStore = useAuthStore()

const investingInvestorStore = useInvestingInvestorStore()

const current_individual = ref<Investor>()

const accreditedModal = useModal()

const disbursementModal = useModal()

const isAdmin = computed(() => authStore.is_site_or_group_admin)

///////////////////////////////////////////////////////////////////////////////
// Statuses
///////////////////////////////////////////////////////////////////////////////

const accreditationBadge = computed(() => accreditatedStatusInfo[current_individual.value?.accredited_status])
const profileBadge = computed(() => profileStatusInfo[current_individual.value?.profile_status])

///////////////////////////////////////////////////////////////////////////////

onMounted(async () => {
  await workspaceStore.fetchCurrent()
  await investingInvestorStore.fetchIndividual(route.params.individual_id)
  current_individual.value = investingInvestorStore.items.get(cid)
  skeleton.value = false
})
</script>

<template>
  <header class="mb-6 px-4 sm:px-0">
    <div class="-mt-8 mb-6">
      <RouterLinkBack
        v-if="isAdmin"
        :to="{ name: 'investing.individuals' }"
        :fallback="{ name: 'investing.individual-overview' }"
      >
        <VButtonInvisible>← Back to all Individuals</VButtonInvisible>
      </RouterLinkBack>
    </div>

    <div class="flex items-center gap-5">
      <RouterLink :to="{ name: 'investing.individual.edit' }">
        <VAvatar :image="current_individual?.profile_picture" :initials="current_individual?.initials" size="lg" />
      </RouterLink>
      <div class="flex max-w-[70%] flex-col gap-3">
        <VSkeletonBar v-if="skeleton" />

        <h2
          v-if="!skeleton"
          class="whitespace-nowrap text-3xl font-semibold leading-7 text-gray-900 dark:text-gray-300"
        >
          {{ current_individual?.name }}
        </h2>
        <div v-if="current_individual?.email" class="-mt-2">
          <VSkeletonBar v-if="skeleton" />
          <VMailTo v-else :email="current_individual?.email" class="incognito text-sm" />
        </div>
        <div class="flex items-center justify-start space-x-1.5 text-xs">
          <VSkeletonBar v-if="skeleton" />
          <VSkeletonBar v-if="skeleton" />
          <VSkeletonBar v-if="skeleton" />

          <a
            v-if="accreditationBadge"
            :href="`${rails_url()}/investors/${current_individual?.id}/edit/accreditation`"
            class="cursor-pointer"
          >
            <VBadge :color="accreditationBadge.color">{{ accreditationBadge.label }}</VBadge>
          </a>

          <ScreeningCheckWrapper
            v-if="current_individual?.screening_check_id"
            :checkId="current_individual?.screening_check_id"
            :status="current_individual?.screening_check_status as Status"
          />

          <template v-if="profileBadge">
            <RouterLink :to="{ name: 'investing.individual.edit' }">
              <VBadge :color="profileBadge.color">{{ profileBadge.label }}</VBadge>
            </RouterLink>
          </template>
        </div>
      </div>
    </div>
    <div class="mb-6 sm:mb-0">
      <div class="flex items-center justify-end space-x-2">
        <slot name="actions"></slot>
      </div>
    </div>

    <VPopUpModal :modalStore="accreditedModal"> Accredited Info </VPopUpModal>

    <VPopUpModal :modalStore="disbursementModal">
      <VSection label="Disbursement Info">
        <table class="disbursement-info w-full">
          <tbody>
            <tr>
              <td class="font-semibold">Method</td>
              <td>{{ current_individual?.disbursement_method }}</td>
            </tr>
            <template v-if="current_individual?.disbursement_method === 'wire'">
              <tr>
                <td class="font-semibold">Bank name</td>
                <td>{{ current_individual?.disbursement_wire_bank_name }}</td>
              </tr>
              <tr>
                <td class="font-semibold">Bank address</td>
                <td>{{ current_individual?.disbursement_wire_bank_address }}</td>
              </tr>
              <tr>
                <td class="font-semibold">Bank routing number</td>
                <td>{{ current_individual?.disbursement_wire_bank_routing_number }}</td>
              </tr>
              <tr>
                <td class="font-semibold">Bank account number</td>
                <td>{{ current_individual?.disbursement_wire_bank_account_number }}</td>
              </tr>
              <tr>
                <td class="font-semibold">Bank swift code</td>
                <td>{{ current_individual?.disbursement_wire_bank_swift_code }}</td>
              </tr>
              <tr>
                <td class="font-semibold">Bank account name</td>
                <td>{{ current_individual?.disbursement_wire_account_name }}</td>
              </tr>
              <tr>
                <td class="font-semibold">For further creadit to</td>
                <td>{{ current_individual?.disbursement_wire_for_further_credit_to }}</td>
              </tr>
            </template>
            <tr v-else-if="current_individual?.disbursement_method === 'check'">
              <td class="font-semibold">Receiver name</td>
              <td>{{ current_individual?.disbursement_check_receiver_name }}</td>
            </tr>
            <tr v-else>
              <td class="font-semibold">Other details</td>
              <td>{{ current_individual?.disbursement_other_details }}</td>
            </tr>
          </tbody>
        </table>
      </VSection>
    </VPopUpModal>
  </header>
</template>

<style scoped>
table.disbursement-info td,
table.disbursement-info th {
  @apply whitespace-nowrap pb-2 pr-5 text-left first:w-[1%];
}
</style>
